import axios from 'axios';
import qs from 'qs';
import { registerBalanceInterceptor } from '../../services/balanceInterceptor';
import { getSourceFilterAdapter } from '../../sources/filterAdapters';
import { useAuthStore } from '../../store/authStore';
import { isMockRelevanceEnabled, mockRelevanceSearch } from './mockRelevance';

const API_BASE_URL =
  process.env.REACT_APP_RELEVANTER_BASE_URL ||
  '/relevanter';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300_000,
  paramsSerializer: function(params) {
    return qs.stringify(params, { 
      arrayFormat: 'brackets', // This will serialize arrays as param[]=value
      skipNulls: true
    });
  }
});

// Request interceptor for auth
api.interceptors.request.use(
  (config) => {
    // Prefer shared auth token from main app store
    let token: string | null = useAuthStore.getState().token;

    // Fallbacks to avoid race conditions on first render/mount
    if (!token) {
      // CRM auth keys
      token = localStorage.getItem('crm_auth_token')
        ?? sessionStorage.getItem('crm_auth_token')
        ?? null;
    }

    // Legacy relevanter keys (backward compatibility)
    if (!token) {
      token = localStorage.getItem('adminToken')
        ?? sessionStorage.getItem('adminToken')
        ?? null;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Обработка auth-ошибок делегирована компонентам
    }
    return Promise.reject(error);
  }
);

// REL-58: показ сообщения о недостатке средств (HTTP 402) и для этого инстанса —
// через него идут проверки кредитов и запуск AI-рассылок (aiRecruiterService).
registerBalanceInterceptor(api);

// Search applicants
export const searchApplicants = async (filters: any, signal?: AbortSignal) => {
  if (filters?.source === 'hh') {
    const response = await api.get('/hh/search', { params: filters, signal });
    return response.data;
  } else {
    const response = await api.get('/applicants', { params: filters, signal });
    return response.data;
  }
};

// Get applicant by ID
export const getApplicantById = async (id: any, source?: string) => {
  const params = source ? { source } : {};
  const response = await api.get(`/applicants/${id}`, { params });
  return response.data;
};

// Search HH resumes
export const searchHHResumes = async (filters: any) => {
  const response = await api.get('/hh/search', { params: filters });
  return response.data;
};

// Get HH resume by ID
export const getHHResume = async (id: any) => {
  const response = await api.get(`/hh/resume/${id}`);
  return response.data;
};

// Open HH resume contacts (paid operation)
export const openHHResumeContacts = async (id: any, options: any = {}) => {
  const response = await api.post(`/hh/resume/${id}/open-contacts`, options);
  return response.data;
};

// Открыть контакты резюме Avito (платная операция)
export const openAvitoResumeContacts = async (id: any) => {
  const response = await api.post(`/avito/resume/${id}/open-contacts`);
  return response.data;
};

// Get HH.ru mail templates
export const getHHMailTemplates = async () => {
  const response = await api.get('/hh/mail-templates');
  return response.data;
};

// Get HH.ru active vacancies
export const getHHActiveVacancies = async () => {
  const response = await api.get('/hh/vacancies');
  return response.data;
};

export interface HHEmployerAddress {
  id: string;
  raw: string;
  name?: string;
  description?: string;
  street?: string;
  building?: string;
  city?: string;
  deleted?: boolean;
  metroStations?: Array<{
    stationId: string;
    stationName?: string;
    lineId?: string;
    lineName?: string;
  }>;
}

export const getHHEmployerAddresses = async (): Promise<{ success: boolean; items: HHEmployerAddress[] }> => {
  const response = await api.get('/hh/employer/addresses');
  return response.data;
};

export const getAtsActiveVacancies = async (params: {
  source: string;
  query?: string;
  page?: number;
  pageSize?: number;
  activeOnly?: boolean;
}) => {
  const response = await api.get('/applicants/vacancies', { params });
  return response.data;
};

// Get HH.ru vacancy by ID (raw HH data)
export const getHHVacancyById = async (vacancyId: string) => {
  const response = await api.get(`/hh/vacancies/${vacancyId}`);
  return response.data;
};

// Get HH.ru vacancy detail mapped to our format (for form auto-fill)
export const getHHVacancyDetail = async (vacancyId: string) => {
  const response = await api.get(`/hh/vacancies/${vacancyId}/detail`);
  return response.data;
};

// Получить вакансию Авито, приведённую к формату нашей формы
export const getAvitoVacancyDetail = async (vacancyId: string) => {
  const response = await api.get(`/avito/vacancies/${vacancyId}/detail`);
  return response.data;
};

// Export (publish) our position data to HH.ru
export const exportVacancyToHH = async (positionData: Record<string, any>) => {
  const response = await api.post('/hh/vacancies/export', { positionData });
  return response.data;
};

// Подсчёт длины итогового описания для HH (единственная точка истины — relevanter)
export const getExportDescriptionLength = async (fields: {
  companyDescription: string;
  description: string;
  socialPackageText: string;
}): Promise<{
  htmlLength: number;
  plainLength: number;
  minLength: number;
  maxLength: number;
  warnings?: string[];
  blocks?: {
    description: { length: number };
    companyDescription: { length: number };
    socialPackageText: { length: number };
  };
}> => {
  const response = await api.post('/hh/vacancies/export-description-length', fields);
  return response.data;
};

// Update existing HH.ru vacancy with our position data
export const updateHHVacancyExport = async (hhVacancyId: string, positionData: Record<string, any>) => {
  const response = await api.put(`/hh/vacancies/${hhVacancyId}/export`, { positionData });
  return response.data;
};

// Batch-проверка кандидатов перед добавлением в рассылку (TASKNEIROKLYUCH-434)
export const checkCampaignCandidates = async (
  candidates: any[],
  companyId: number | null,
  positionId?: string | number | null,
  // REL-326: hhVacancyId нужен, чтобы бэкенд спросил у HH, кто уже откликался на
  // эту вакансию. Без него «Уже откликался» выясняется только при отправке.
  options?: { hhVacancyId?: string | number | null }
) => {
  const hhVacancyId = options?.hhVacancyId ?? null;
  const response = await api.post('/ai-recruiter/campaigns/check-candidates', {
    candidates,
    companyId,
    ...(positionId ? { positionId } : {}),
    ...(hhVacancyId ? { hhVacancyId } : {})
  });
  return response.data;
};

// Send HH.ru invitation to candidate
export const sendHHInvitation = async (data: any) => {
  const response = await api.post('/hh/send-invitation', data);
  return response.data;
};

// ============= HH Negotiations API =============

// Send HH negotiation invitation (first contact)
export const sendHHNegotiationInvite = async (data: { resumeId: string; vacancyId: string; message: string }) => {
  const response = await api.post('/hh/negotiations/invite', data);
  return response.data;
};

// Send message in existing HH negotiation
export const sendHHNegotiationMessage = async (negotiationId: string, message: string) => {
  const response = await api.post(`/hh/negotiations/${negotiationId}/messages`, { message });
  return response.data;
};

// Get messages for a specific HH negotiation
export const getHHNegotiationMessages = async (negotiationId: string) => {
  const response = await api.get(`/hh/negotiations/${negotiationId}/messages`);
  return response.data;
};

// Poll for new HH negotiation updates
export const pollHHNegotiationUpdates = async (vacancyId: string) => {
  const response = await api.get('/hh/negotiations/updates', { params: { vacancy_id: vacancyId } });
  return response.data;
};

// Send emails to candidates
export const sendEmails = async (candidates: any, templateData: any) => {
  const response = await api.post('/hh/send-emails', {
    candidates,
    ...templateData,
  });
  return response.data;
};

// Export data
export const exportData = async (data: any, format: any = 'csv') => {
  const endpoint = format === 'excel' ? '/export/excel' : '/export/csv';
  const response = await api.post(endpoint, { data }, {
    responseType: 'blob',
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `applicants.${format === 'excel' ? 'xlsx' : 'csv'}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Check applicants availability
export const checkApplicantsAvailability = async (applicantIds: any) => {
  const response = await api.post('/applicants/check-availability', { applicantIds });
  return response.data;
};

// Check HH.ru authorization status
export const checkHHStatus = async () => {
  const response = await api.get('/auth/hh/status');
  return response.data;
};

// Generate AI comment for resume
export const generateAIComment = async (resumeData: any, searchFilters: any) => {
  const response = await api.post('/ai/generate-comment', {
    resumeData,
    searchFilters
  });
  return response.data;
};

export const generateAICommentsBatch = async (resumes: any[], searchFilters: any) => {
  const response = await api.post('/ai/generate-comments-batch', {
    resumes,
    searchFilters
  });
  return response.data;
};

// Search HH resumes with LLM relevance scoring and auto-fetch.
// TASKNEIROKLYUCH-645: при активном флаге mockRelevance (env или localStorage)
// относит запрос в локальную заглушку — см. mockRelevance.ts.
export const searchHHWithRelevance = async (filters: any, signal?: AbortSignal) => {
  if (isMockRelevanceEnabled()) {
    return mockRelevanceSearch(filters, signal, searchApplicants);
  }
  const response = await api.post('/hh/search-with-relevance', filters, { signal });
  return response.data;
};

// Поиск E-Staff кандидатов с AI scoring релевантности.
// TASKNEIROKLYUCH-645: мок-фолбэк аналогично HH-ветке.
export const searchEStaffWithRelevance = async (filters: any, signal?: AbortSignal) => {
  if (isMockRelevanceEnabled()) {
    return mockRelevanceSearch(filters, signal, searchApplicants);
  }
  const response = await api.post('/applicants/search-with-relevance', filters, { signal });
  return response.data;
};

/**
 * Поиск резюме Avito.
 *
 * Делегирует сбор query-параметров Avito source-module. Здесь остаётся только
 * транспорт до /api/avito/search, а список полей и snake_case-имена живут в
 * src/sources/avito/filterAdapter.ts.
 *
 * Multi-enum (educationLevel, schedule, businessTripReadiness и т.п.)
 * передаются CSV-строкой через запятую — Avito принимает именно такой
 * формат для нескольких значений.
 *
 * Не подключено: radius (нужен UI карты), несколько location/metro/district
 * (сгенерированный openapi-клиент avito типизирует как number, multi нужно
 * костылём через ручной axios — отдельная задача).
 */
export const searchAvitoResumes = async (filters: any, signal?: AbortSignal) => {
  const adapter = getSourceFilterAdapter('avito');
  if (!adapter) {
    throw new Error('Avito filter adapter не зарегистрирован');
  }
  const params = adapter.toApiParams(filters || {});

  const response = await api.get('/avito/search', { params, signal });
  return response.data;
};

// HTTP-fallback отмены: используется если WebSocket не подключён
// (cancel-search через WS — основной канал, см. useSearchProgress.cancelSearch).
export const cancelSearchSession = async (searchId: string): Promise<void> => {
  await api.post(`/searches/${encodeURIComponent(searchId)}/cancel`);
};

// Parse job description with AI
export const parseJobDescription = async (description: any) => {
  const response = await api.post('/ai/parse-job-description', {
    description
  });
  return response.data;
};

// Statistics API methods
export const getStatisticsOverview = async (period: string = 'all') => {
  const response = await api.get('/statistics/overview', { params: { period } });
  return response.data;
};

export const getStatisticsSearchesByUser = async (period: string = 'all') => {
  const response = await api.get('/statistics/searches/by-user', { params: { period } });
  return response.data;
};

export const getUserStatistics = async (userId: number) => {
  const response = await api.get(`/statistics/user/${userId}`);
  return response.data;
};

export const getStatisticsSearches = async () => {
  const response = await api.get('/statistics/searches');
  return response.data;
};

export const getStatisticsLogins = async () => {
  const response = await api.get('/statistics/logins');
  return response.data;
};

// HH token endpoints (managed on Relevanter backend)
export const getHHToken = async () => {
  const response = await api.get('/tokens/HH');
  return response.data; // { userId, type: 'HH', tokenMasked, updatedAt }
};

export const updateHHToken = async (token: any) => {
  const response = await api.put('/tokens/HH', { token });
  return response.data; // { userId, type: 'HH', updatedAt }
};

// HR-Proxy (E-Staff/Skillaz) token endpoints — per-company
export const getHRProxyToken = async () => {
  const response = await api.get('/tokens/company/HR_PROXY');
  return response.data;
};

export const updateHRProxyToken = async (token: string) => {
  const response = await api.put('/tokens/company/HR_PROXY', { token });
  return response.data;
};

export const deleteHRProxyToken = async () => {
  const response = await api.delete('/tokens/company/HR_PROXY');
  return response.data;
};

export const getAtsIntegrations = async () => {
  const response = await api.get('/integrations/ats');
  return response.data;
};

export const saveAtsIntegration = async (payload: { source: string; apiUrl: string; apiToken: string }) => {
  const response = await api.put('/integrations/ats', payload);
  return response.data;
};

export const deleteAtsIntegration = async (source: string) => {
  const response = await api.delete(`/integrations/ats/${encodeURIComponent(source)}`);
  return response.data;
};

export const getAtsConnectionStatus = async (source: string) => {
  const response = await getAtsIntegrations();
  return response?.configurations?.[source?.toLowerCase?.() || ''] || null;
};

// FR-2.5 + FR-3.11: Динамический список доступных источников
export const getSources = async () => {
  const response = await api.get('/sources');
  return response.data;
};

// FR-2.6: Справочники через HR-Proxy
export const getHRProxyDictionaries = async (source: string) => {
  const response = await api.get('/applicants/dictionaries', { params: { source } });
  return response.data;
};

export interface HRProxyDictionaryItem {
  id: string;
  name: string;
  stateId?: string;
  [key: string]: unknown;
}

export interface HRProxyDictionaryResponse {
  items?: HRProxyDictionaryItem[];
  total?: number;
  estaffDisabled?: boolean;
  candidateSourcesEndpointMissing?: boolean;
  eventTypesEndpointMissing?: boolean;
  [key: string]: unknown;
}

export interface EstaffStatusMapping {
  contactEstablished: string | null;
  hrInterviewScheduled: string | null;
  aiScreeningPassed: string | null;
}

export interface EstaffStatusAction {
  key: keyof EstaffStatusMapping;
  action: string;
  label: string;
  statusId: string | null;
  stateId: string | null;
  stateName: string | null;
  changesStatus: boolean;
  allowNoStatus: boolean;
  noStatusLabel?: string | null;
  settingsSource?: 'settings' | 'default' | string;
  dictionaryAvailable?: boolean;
}

function hasHttpStatus(error: unknown, status: number): boolean {
  return typeof error === 'object'
    && error !== null
    && 'response' in error
    && (error as { response?: { status?: number } }).response?.status === status;
}

export interface EstaffCompanySettings {
  companyId: number;
  sourceId: string | null;
  contactEstablishedStatusId?: string | null;
  hrInterviewStatusId?: string | null;
  screeningStatusId: string | null;
  attachmentTypeId: string;
  statusMapping?: EstaffStatusMapping;
  statusActions?: EstaffStatusAction[];
  exists?: boolean;
}

export const getEstaffCompanySettings = async (companyId: number): Promise<EstaffCompanySettings> => {
  const response = await api.get(`/estaff/company-settings/${encodeURIComponent(String(companyId))}`);
  return response.data;
};

export const saveEstaffCompanySettings = async (
  companyId: number,
  payload: Partial<Pick<EstaffCompanySettings, 'sourceId' | 'screeningStatusId' | 'attachmentTypeId' | 'statusMapping'>>
): Promise<EstaffCompanySettings> => {
  const response = await api.put(`/estaff/company-settings/${encodeURIComponent(String(companyId))}`, payload);
  return response.data;
};

export const getEstaffCandidateSources = async (companyId: number): Promise<HRProxyDictionaryResponse | HRProxyDictionaryItem[]> => {
  try {
    const response = await api.get(`/estaff/company-settings/${encodeURIComponent(String(companyId))}/candidate-sources`);
    return response.data;
  } catch (error) {
    // Совместимость раздельного деплоя: старый backend еще не знает этот endpoint.
    if (hasHttpStatus(error, 404)) {
      return { items: [], total: 0, candidateSourcesEndpointMissing: true };
    }
    throw error;
  }
};

// TASKNEIROKLYUCH-697: autocomplete для фильтра «компании-доноры».
export const getEstaffEventTypes = async (companyId: number): Promise<HRProxyDictionaryResponse | HRProxyDictionaryItem[]> => {
  try {
    const response = await api.get(`/estaff/company-settings/${encodeURIComponent(String(companyId))}/event-types`);
    return response.data;
  } catch (error) {
    if (hasHttpStatus(error, 404)) {
      return {
        items: [],
        total: 0,
        eventTypesEndpointMissing: true,
      };
    }
    throw error;
  }
};

export interface DonorCompanySuggestion {
  /**
   * Уникальное значение `candidates.last_job_org_name` из mirror (точное
   * содержимое поля «текущая работа» кандидата). После выбора пункта
   * фильтр `donorCompanies` будет содержать ровно эту строку → backend ILIKE
   * матчит её как exact match.
   */
  value: string;
  /** Количество кандидатов в mirror с этим `last_job_org_name`. */
  count: number;
}
export interface DonorCompaniesSuggestResponse {
  items: DonorCompanySuggestion[];
  total: number;
}
export const suggestDonorCompanies = async (
  source: string,
  q: string,
  limit: number = 20,
  signal?: AbortSignal,
): Promise<DonorCompaniesSuggestResponse> => {
  const response = await api.get('/applicants/donors/suggest', {
    params: { source, q, limit },
    signal,
  });
  return response.data;
};

// Vacancies API methods (restored from old code)
export const getVacanciesList = async (page: number = 1, perPage: number = 20) => {
  const response = await api.get('/vacancies/list', {
    params: { page, per_page: perPage }
  });
  return response.data;
};

export interface ResponsesFilters {
  search_text?: string;
  show_only_new_responses?: boolean;
  age_from?: string;
  age_to?: string;
  only_with_age?: boolean;
  area?: string[];
  relocation?: string;
  experience?: string[];
  education_level?: string[];
  gender?: string;
  salary_from?: string;
  salary_to?: string;
  currency?: string;
  order_by?: string;
  language?: string[];
  citizenship?: string[];
  driver_license_types?: string[];
  has_vehicle?: boolean;
  collection?: string;
  desired_position?: string;
  current_position?: string;
}

export const getVacancyResponses = async (
  vacancyId: string, 
  page: number = 1, 
  perPage: number = 20,
  filters?: ResponsesFilters
) => {
  const params: any = { page, per_page: perPage };
  
  if (filters) {
    if (filters.search_text) params.search_text = filters.search_text;
    if (filters.show_only_new_responses) params.show_only_new_responses = 'true';
    if (filters.age_from) params.age_from = filters.age_from;
    if (filters.age_to) params.age_to = filters.age_to;
    if (filters.only_with_age) params.only_with_age = 'true';
    if (filters.area && filters.area.length > 0) params.area = filters.area;
    if (filters.relocation) params.relocation = filters.relocation;
    if (filters.experience && filters.experience.length > 0) params.experience = filters.experience;
    if (filters.education_level && filters.education_level.length > 0) params.education_level = filters.education_level;
    if (filters.gender) params.gender = filters.gender;
    if (filters.salary_from) params.salary_from = filters.salary_from;
    if (filters.salary_to) params.salary_to = filters.salary_to;
    if (filters.currency) params.currency = filters.currency;
    if (filters.order_by) params.order_by = filters.order_by;
    if (filters.language && filters.language.length > 0) params.language = filters.language;
    if (filters.citizenship && filters.citizenship.length > 0) params.citizenship = filters.citizenship;
    if (filters.driver_license_types && filters.driver_license_types.length > 0) params.driver_license_types = filters.driver_license_types;
    if (filters.has_vehicle) params.has_vehicle = 'true';
    if (filters.collection) params.collection = filters.collection;
    if (filters.desired_position) params.desired_position = filters.desired_position;
    if (filters.current_position) params.current_position = filters.current_position;
  }

  const response = await api.get(`/vacancies/${vacancyId}/responses`, { params });
  return response.data;
};

export interface AvitoVacancyApplicationsFilters {
  state?: string;
  isViewed?: boolean;
  searchText?: string;
  ageFrom?: string;
  ageTo?: string;
  salaryFrom?: string;
  salaryTo?: string;
  gender?: string;
  location?: string[];
  citizenship?: string[];
  educationLevel?: string[];
  specialization?: string[];
  schedule?: string[];
  businessTripReadiness?: string[];
  relocationReadiness?: string[];
  experienceMin?: string;
  experienceMax?: string;
  driverLicence?: string;
  driverLicenceCategory?: string[];
  desiredPosition?: string;
  currentPosition?: string;
  orderBy?: string;
}

const toCsvParam = (value: unknown): string | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const csv = value.map((item) => String(item).trim()).filter(Boolean).join(',');
  return csv || undefined;
};

const toAvitoLocationCsv = (value: unknown): string | undefined => {
  const rawList = Array.isArray(value) ? value : [value];
  const csv = rawList
    .filter((item) => item !== undefined && item !== null)
    .flatMap((item) => String(item).split(','))
    .map((item) => item.trim().split('/').pop()?.trim())
    .filter(Boolean)
    .join(',');
  return csv || undefined;
};

export const getAvitoVacancyApplications = async (
  vacancyId: string,
  filters?: AvitoVacancyApplicationsFilters,
  page?: number,
  perPage?: number
) => {
  const params: any = {};
  if (page) params.page = page;
  if (perPage) params.per_page = perPage;
  if (filters?.state) params.state = filters.state;
  if (typeof filters?.isViewed === 'boolean') params.is_viewed = String(filters.isViewed);
  if (filters?.searchText) params.search_text = filters.searchText;
  if (filters?.ageFrom) params.age_min = filters.ageFrom;
  if (filters?.ageTo) params.age_max = filters.ageTo;
  if (filters?.salaryFrom) params.salary_min = filters.salaryFrom;
  if (filters?.salaryTo) params.salary_max = filters.salaryTo;
  if (filters?.gender === 'M' || filters?.gender === 'male') params.gender = 'male';
  if (filters?.gender === 'F' || filters?.gender === 'female') params.gender = 'female';
  const location = toAvitoLocationCsv(filters?.location);
  if (location) params.location = location;
  const citizenship = toCsvParam(filters?.citizenship);
  if (citizenship) params.nationality = citizenship;
  const educationLevel = toCsvParam(filters?.educationLevel);
  if (educationLevel) params.education_level = educationLevel;
  const specialization = toCsvParam(filters?.specialization);
  if (specialization) params.specialization = specialization;
  const schedule = toCsvParam(filters?.schedule);
  if (schedule) params.schedule = schedule;
  const businessTrip = toCsvParam(filters?.businessTripReadiness);
  if (businessTrip) params.business_trip_readiness = businessTrip;
  const relocation = toCsvParam(filters?.relocationReadiness);
  if (relocation) params.relocation_readiness = relocation;
  if (filters?.experienceMin) params.experience_min = filters.experienceMin;
  if (filters?.experienceMax) params.experience_max = filters.experienceMax;
  if (filters?.driverLicence) params.driver_licence = filters.driverLicence;
  const driverCategories = toCsvParam(filters?.driverLicenceCategory);
  if (driverCategories) params.driver_licence_category = driverCategories;
  if (filters?.desiredPosition) params.desired_position = filters.desiredPosition;
  if (filters?.currentPosition) params.current_position = filters.currentPosition;
  if (filters?.orderBy) params.order_by = filters.orderBy;

  const response = await api.get(`/avito/vacancies/${vacancyId}/applications`, { params });
  const items = Array.isArray(response.data?.items) ? response.data.items : [];
  return {
    ...response.data,
    items: items.map((item: any) => ({
      ...item,
      id: item.id ?? item.application_id,
      applicationSource: item.source,
      source: 'avito',
      sourceType: 'avito',
      sourceVacancyId: item.sourceVacancyId ?? item.vacancy_id ?? vacancyId,
      created_at: item.created_at,
      updated_at: item.updated_at,
      resume: item.resume || null,
    })),
  };
};

export const getAvitoApplicationStates = async () => {
  const response = await api.get('/avito/applications/states');
  const items = Array.isArray(response.data?.items) ? response.data.items : [];
  return items
    .map((item: any) => {
      if (typeof item === 'string') {
        return { id: item, name: item };
      }
      const id = item?.id ?? item?.value ?? item?.state ?? item?.code ?? item?.slug;
      if (id === undefined || id === null || String(id).trim() === '') return null;
      return {
        ...item,
        id: String(id),
        name: item?.name ?? item?.title ?? item?.description ?? String(id),
      };
    })
    .filter(Boolean);
};

export const markAvitoApplicationViewed = async (applicationId: string, isViewed: boolean = true) => {
  const response = await api.post(`/avito/applications/${applicationId}/viewed`, {
    is_viewed: isViewed,
  });
  return response.data;
};

export const applyAvitoApplicationAction = async (applicationIds: string[], action: string) => {
  const response = await api.post('/avito/applications/actions', {
    ids: applicationIds,
    action,
  });
  return response.data;
};

export const getBatchVacancyResponses = async (vacancyIds: string[], page: number = 1, perPage: number = 50) => {
  const response = await api.post('/vacancies/batch-responses', {
    vacancyIds,
    page,
    per_page: perPage
  });
  return response.data;
};

export const getBatchVacancyResponsesPaged = async (
  vacancyIds: string[],
  perPage: number = 10,
  filters?: ResponsesFilters,
  options?: { noCache?: boolean }
) => {
  const response = await api.post('/vacancies/batch-responses', {
    vacancyIds,
    per_page: perPage,
    filters: filters || {},
    no_cache: options?.noCache === true
  });
  return response.data;
};

export const evaluateResume = async (vacancyId: string, resumeData: any, baseVacancy?: any, scoringEnabled?: boolean) => {
  const response = await api.post(`/vacancies/${vacancyId}/evaluate-resume`, {
    resumeData,
    baseVacancy: baseVacancy || undefined,
    scoringEnabled
  });
  return response.data;
};

export const getFullResume = async (resumeId: string) => {
  const response = await api.get(`/vacancies/resume/${resumeId}`);
  return response.data;
};

export const getBatchResumes = async (resumeIds: string[]) => {
  const response = await api.post('/vacancies/resumes/batch', { resumeIds });
  return response.data;
};

export const markVacancyResponseViewed = async (vacancyId: string, responseId: string) => {
  const response = await api.post(`/vacancies/${vacancyId}/responses/${responseId}/viewed`);
  return response.data;
};

export const findRelevantVacancyResponses = async (params: {
  vacancyIds: string[];
  targetCount: number;
  minRating?: number | null;
  maxRating?: number | null;
  filters?: ResponsesFilters;
  orderBy?: string;
  baseVacancy?: any;
  scoringEnabled?: boolean;
  searchId?: string;
}, signal?: AbortSignal) => {
  const response = await api.post('/vacancies/batch-responses/find-relevant', {
    vacancyIds: params.vacancyIds,
    targetCount: params.targetCount,
    minRating: params.minRating ?? null,
    maxRating: params.maxRating ?? null,
    filters: params.filters || {},
    order_by: params.orderBy,
    baseVacancy: params.baseVacancy || undefined,
    scoringEnabled: params.scoringEnabled,
    searchId: params.searchId
  }, { signal });
  return response.data;
};

export default api;
